import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import PollenDotsMap from '@/components/PollenDotsMap';
import PollenDotsContactFooter from '@/components/PollenDotsContactFooter';
import PollenDotsLegend from '@/components/PollenDotsLegend';
import ShareMapMenu from '@/components/ShareMapMenu';
import { StatCard } from '@/pages/PublicJourneyShare';
import { MapPin, Users, Loader2, CalendarDays, Sparkles } from 'lucide-react';

/**
 * Public share page for a Guide or Driver. Mirrors the customer journey
 * layout so the brand reads as one system — but celebrates the CREW member
 * instead of the traveller. Drops all PII (traveller names & contact data
 * remain internal); exposes only counts and verified map pins.
 */
const PublicVendorShare = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const mapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/vendor-journey/${token}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'Share link not found or revoked.');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const firstName = (data.vendor_name || 'Crew').split(' ')[0];
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const filename = `${firstName.toLowerCase()}-pollen-dots`;

  // Map pins for vendors: PLACES only. Guides & drivers relate to trails and
  // spots, not hotels — so hotel pins are intentionally omitted here. Hotels
  // still count in the operator-facing tour history, but don't clutter the
  // public page's dots-of-connection story.
  const pins = data.places.map(p => ({ ...p, type: 'place' }));

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_left,rgba(255,237,213,.55),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(220,252,231,.45),transparent_60%)]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex justify-center pt-2">
          <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-7 sm:h-8 w-auto" data-testid="share-brand-logo" />
        </div>

        <header className="text-center mt-8 mb-6">
          <h1 className="text-2xl sm:text-4xl font-medium tracking-tight leading-tight text-foreground/90" data-testid="share-vendor-name">
            <span className="italic font-serif font-normal">{firstName}</span>
            <span>&rsquo;s dots of connection</span>
          </h1>
          <p className="text-[10px] tracking-[0.26em] uppercase text-muted-foreground/80 mt-3">
            Made with Pollen Dots
          </p>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto mt-3 leading-relaxed">
            Every trail walked, every hidden spot discovered, every traveller carried &mdash; gently marked as dots on a map.
          </p>
          {data.vendor_location && (
            <p className="text-xs text-muted-foreground/80 mt-2 inline-flex items-center gap-1 justify-center">
              <MapPin className="w-3 h-3" /> Based in {data.vendor_location}
            </p>
          )}
        </header>

        <div className="relative rounded-xl overflow-hidden border border-amber-200/70 bg-white shadow-sm">
          <PollenDotsMap ref={mapRef} pins={pins} height={360} />
          {shareUrl && (
            <div className="absolute right-3 bottom-3 z-[500]">
              <ShareMapMenu
                mapRef={mapRef}
                shareUrl={shareUrl}
                whatsappText={`${data.vendor_name}'s dots of connection — every trail walked, every hidden spot discovered, every traveller carried. Made with Pollen Dots.`}
                filename={filename}
              />
            </div>
          )}
        </div>
        <PollenDotsLegend items={[{ color: '#0d9488', label: 'Places', testid: 'legend-places' }]} />

        <div className="grid grid-cols-2 gap-3 mt-6">
          <StatCard Icon={CalendarDays} tint="violet" label="Days"       value={data.days_served}       testid="vendor-days" />
          <StatCard Icon={Sparkles}     tint="orange" label="Tours"      value={data.tours_count}       testid="vendor-tours" />
          <StatCard Icon={Users}        tint="rose"   label="Travellers" value={data.travellers_served} testid="vendor-travellers" />
          <StatCard Icon={MapPin}       tint="teal"   label="Places"     value={data.places.length}     testid="vendor-places" />
        </div>

        <PollenDotsContactFooter />
      </div>
    </div>
  );
};

export default PublicVendorShare;
